import {
  ApplicationRef,
  Component,
  ComponentRef,
  EnvironmentInjector,
  EventEmitter,
  OnDestroy,
  createComponent,
  inject,
  signal
} from "@angular/core";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { lastValueFrom, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { Staff } from "src/app/backend/models/staff/staff";
import { ReportDesignerItemCustom } from "src/app/modules/bootstrap/modules/report/report-design-item";
import { ReportDesignerCustomComponentInstance } from "src/app/modules/bootstrap/modules/report/report-designer/report-designer-custom-component-instance";
import { SoapDesign } from "src/app/backend/models/soap/soap-design";
import { MemberLight } from "src/app/backend/models/member/member";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { createNewPrescriptionFunctionWithCustomerAndPatient } from "src/app/modules/bootstrap/pms-ui/functions/create-new-prescription.function";
import { PrescriptionBackend } from "src/app/backend/api/prescription.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PopupService } from "src/app/services/popup/popup.service";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { Pet } from "src/app/backend/models/pet/pet";
import { Customer } from "src/app/backend/models/customer/customer";
import { TabService } from "src/app/services/tab.service";
import { routes } from "src/app/routing/routes";
import { MedicalServiceBackend } from "src/app/backend/api/medical-service/medical-service.backend";
import { Bind } from "src/app/support/decorators/bind";
import { Drug } from "src/app/backend/models/drug";
import { BlockDrugComponent } from "src/app/modules/bootstrap/pms-ui/components/ui/block-drug/block-drug.component";
import { registerPatientWeightFunction } from "../../../../../functions/register-patient-weight.functions";
import { getLastWeight } from "../../../../../../../../support/patient";
import { isToday } from "../../../../../../../../support/date";
import { ModuleService } from "src/app/services/feature-access-control/module.service";

import { TranslocoModule } from "@jsverse/transloco";
import { ButtonModule } from "src/app/components/ui/button/button.module";
import { DxFormModule } from "devextreme-angular";
import { SoapTemplateModule } from "src/app/modules/bootstrap/pms-ui/modules/organization/soap-template/soap-template.module";
import { Category } from "src/app/backend/models/tag/tag";

type FormData = {
  title: string;
  medicalServiceId?: string;
  services: string[];
  staff: MemberLight[];
  internalNote: string | undefined;
  tags?: string[];
  organs?: string[];
  diagnosis?: string[];
  anamnesis?: string[];
  physicalExam?: string[];
  laboratoryExams?: string[];
  imagingDiagnostics?: string[];
  weight?: string;
};

export type SoapSummaryConfigData = FormData;

@Component({
  standalone: true,
  imports: [TranslocoModule, ButtonModule, DxFormModule, SoapTemplateModule],
  selector: "pms-soap-template-summary",
  templateUrl: "soap-template-summary.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ]
})
export class SoapTemplateSummaryComponent
  implements ReportDesignerCustomComponentInstance, OnDestroy
{
  action$: EventEmitter<string> = new EventEmitter<string>();

  mode: "default" | "placeholder" = "default";
  editMode: "soap" | "none" = "soap";

  hideInfoInternalData = false;

  templateEmpty = false;

  healthRecordId: string | undefined;
  customer: Customer | undefined;
  patient: Pet | undefined;

  formData: FormData = {
    title: "",
    internalNote: "",
    medicalServiceId: "",
    services: [],
    staff: [],
    tags: [],
    organs: [],
    diagnosis: [],
    anamnesis: [],
    physicalExam: [],
    laboratoryExams: [],
    imagingDiagnostics: [],
    weight: ""
  };

  staffDataSource: DataSource;

  data$: Subject<FormData | null>;
  pet: any;

  preview: boolean = false;

  readOnly: boolean = false;
  hideCustomerAndPatientData: boolean = false;

  isCreatedToday = signal<boolean>(true);

  readonly Category = Category;

  private readonly memberBackend = inject(MemberBackend);
  private readonly tabService = inject(TabService);
  private readonly applictionRef = inject(ApplicationRef);
  private readonly prescriptionBackend = inject(PrescriptionBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly patientBackend = inject(PetBackend);
  private readonly medicalServiceBackend = inject(MedicalServiceBackend);
  private readonly popupService = inject(PopupService);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly moduleService = inject(ModuleService);

  set item(value: ReportDesignerItemCustom) {
    this.mode = value.baseConfig?.mode || "default";
    this.editMode = value.baseConfig?.editMode || "soap";

    this.hideInfoInternalData = value.baseConfig?.hideInfoInternalData || false;

    this.templateEmpty = value.baseConfig?.templateEmpty || false;

    this.healthRecordId = value.baseConfig?.healthRecord?.id;
    this.customer = value.baseConfig?.customer;
    this.patient = value.baseConfig?.patient;

    this.preview = value.baseConfig?.preview;

    this.readOnly = value.baseConfig?.readOnly || false;
    this.hideCustomerAndPatientData =
      value.baseConfig?.hideCustomerAndPatientData || false;

    this.formData.medicalServiceId =
      value.baseConfig?.medicalService?.id || this.formData.medicalServiceId;
    this.formData.title = value.baseConfig?.title || this.formData.title || "";
    this.formData.internalNote =
      value.baseConfig?.internalNote || this.formData.internalNote;
    this.formData.services =
      value.baseConfig?.services || this.formData.services;
    this.formData.staff = value.baseConfig?.staff || this.formData.staff;
    this.formData.diagnosis = value.baseConfig?.diagnosis || [];
    this.formData.organs = value.baseConfig?.organs || [];
    this.formData.anamnesis = value.baseConfig?.anamnesis || [];
    this.formData.physicalExam = value.baseConfig?.physicalExam || [];
    this.formData.laboratoryExams = value.baseConfig?.laboratoryExams || [];
    this.formData.imagingDiagnostics =
      value.baseConfig?.imagingDiagnostics || [];
    this.formData.tags = value.baseConfig?.tags || [];
    this.formData.weight = isToday(value.baseConfig?.healthRecord?.createdAt)
      ? getLastWeight(this.patient!).toString() || ""
      : value.baseConfig.weight;
    this.designModel = value.baseConfig.soapDesignModel;
    this.isCreatedToday.set(isToday(value.baseConfig?.healthRecord?.createdAt));
  }

  designModel: SoapDesign | undefined;

  private componentRefsToDestroy: ComponentRef<any>[] = [];

  constructor() {
    this.data$ = new Subject();

    this.staffDataSource = new DataSource<FormData | null>({
      store: new CustomStore({
        key: "id",
        byKey: (id: string) => this.memberBackend.load(id).toPromise(),
        load: () =>
          this.memberBackend
            .listActive({
              page: 1,
              pageSize: 10000,
              filterValues: { type: "human", system: "false" }
            })
            .pipe(map((x) => x.docs))
            .toPromise()
      })
    });
  }

  ngOnDestroy(): void {
    for (const componentRef of this.componentRefsToDestroy) {
      componentRef.destroy();
    }
  }

  onFieldDataChanged(): void {
    this.data$.next(this.formData);
  }

  onReports(): void {
    this.action$.emit("reports");
  }

  async onCreateWorkOrder(): Promise<void> {
    await this.tabService.open(routes.organization.work_orders.list());
  }

  async createOrAddToCart(pa: any): Promise<void> {}

  calculateDisplayExprTagBoxStaff(staff: Staff | null): string {
    if (!staff) {
      return "";
    }
    return `${staff.firstName || ""} ${staff.lastName || ""}`;
  }

  onTagsUpdate(tags: string[]): void {
    this.formData.tags = tags;
    this.data$.next(this.formData);
  }

  async onCreatePrescription(
    buttonClickAsyncEvent: ButtonClickAsyncEvent
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await createNewPrescriptionFunctionWithCustomerAndPatient(
        this.customer,
        this.patient,
        {
          applicationRef: this.applictionRef,
          bootstrapService: this.bootstrapService,
          customerBackend: this.customerBackend,
          memberBackend: this.memberBackend,
          patientBackend: this.patientBackend,
          popupService: this.popupService,
          prescriptionBackend: this.prescriptionBackend,
          tabService: this.tabService
        },
        {
          fromVisit: this.healthRecordId
        }
      );
    });
  }

  @Bind
  onDrugItemTemplate(drug: Drug, index: number, element: HTMLElement): void {
    const componentRef = createComponent(BlockDrugComponent, {
      environmentInjector: this.environmentInjector
    });

    componentRef.setInput("drug", drug);

    element.append(componentRef.location.nativeElement);
    this.applictionRef.attachView(componentRef.hostView);

    this.componentRefsToDestroy.push(componentRef);
  }

  @Bind
  onProductItemTemplate(drug: Drug, index: number, element: HTMLElement): void {
    const componentRef = createComponent(BlockDrugComponent, {
      environmentInjector: this.environmentInjector
    });

    componentRef.setInput("drug", drug);

    element.append(componentRef.location.nativeElement);
    this.applictionRef.attachView(componentRef.hostView);

    this.componentRefsToDestroy.push(componentRef);
  }

  async onRegisterWeight(): Promise<void> {
    await registerPatientWeightFunction(
      this.patient!,
      this.patientBackend,
      this.popupService
    );
    const pet = await lastValueFrom(
      this.patientBackend.load({ id: this.patient!.id })
    );
    this.formData.weight = getLastWeight(pet).toString();
    this.onFieldDataChanged();
    const patientLineBLocks = this.designModel!.patientLine.split("•");
    this.designModel!.patientLine = [
      ...patientLineBLocks.slice(0, -1),
      `${this.formData.weight} kg`
    ].join("•");
  }
}
